'use client';

import React, { useState, useEffect } from 'react';
import {
  AYAT_BRIDGE,
  SEASONAL,
  DEEP_DIVE,
  WARD_IRL,
  CTX_BRIDGE,
  getSeason,
  getSubmissions,
  saveSubmission,
  CaretakerSubmission,
  WardEncounter,
} from './irlData';
import { ECO_TILES, BIOMES, TEMPORAL, EXTENDED_NAMES } from './ecoData';
import type { EcoTile } from './irlData';

interface IRLLensExpandedProps {
  wardId: string;
  wardName: string;
  cityId: string;
  foodDesertPct: number;
  resourceCategories: string[];
}

// Merge AYAT_BRIDGE + EXTENDED_NAMES for full 37-Name lookup
const ALL_NAMES = { ...AYAT_BRIDGE, ...EXTENDED_NAMES };

// ─── TEMPORAL SLIDER ─────────────────────────────────────────────────────────

function TemporalSlider({ currentIdx = 5 }: { currentIdx?: number }) {
  const [active, setActive] = useState(currentIdx);
  return (
    <div className="mt-3 border border-white/10 p-3 bg-white/3">
      <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">
        TEMPORAL FRAMEWORK — HUDUTH ZAMANI — WHEN DETERMINES WHAT
      </div>
      <div className="flex gap-px mb-3 overflow-x-auto">
        {TEMPORAL.map((stage, i) => (
          <button
            key={stage.k}
            onClick={() => setActive(i)}
            className={`flex-1 min-w-[60px] px-1 py-1.5 text-center border transition-colors ${
              i === active
                ? 'bg-white text-black border-white'
                : i === 5
                ? 'border-white/30 text-white/50 hover:border-white/50' :'border-white/10 text-white/25 hover:border-white/30'
            }`}
          >
            <div className="font-serif text-xs leading-tight">{stage.ar}</div>
            <div className="font-mono text-[7px] uppercase tracking-wider mt-0.5">{stage.k}</div>
          </button>
        ))}
      </div>
      {TEMPORAL[active] && (
        <div className="space-y-1">
          <div className="font-mono text-[9px] font-black text-white uppercase">{TEMPORAL[active].k} — {TEMPORAL[active].ar}</div>
          <p className="font-mono text-[9px] text-white/60 leading-relaxed">{TEMPORAL[active].d}</p>
          <p className="font-mono text-[9px] text-white/30 italic">{TEMPORAL[active].ex}</p>
        </div>
      )}
    </div>
  );
}

// ─── ECO TILE DETAIL ─────────────────────────────────────────────────────────

function EcoTileDetail({
  tile,
  onClose,
  onBridgeClick,
}: {
  tile: EcoTile;
  onClose: () => void;
  onBridgeClick: (tileId: string) => void;
}) {
  const [section, setSection] = useState(0);
  const sections = ['ZAHIR', 'THREE QUESTIONS', 'SUNNAT ALLAH', 'ED / DESIGN', 'AMANAH'];
  const catColor: Record<string, string> = { Creation: '#2d7a47', Process: '#1a6b4e' };
  const tileColor = catColor[tile.cat] || '#2d7a47';

  return (
    <div className="border border-white/15 p-4 space-y-3" style={{ borderLeftColor: tileColor, borderLeftWidth: 3 }}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: tileColor }}>
            {tile.cat} — {tile.biome.toUpperCase()}
          </div>
          <div className="font-mono text-sm font-black text-white uppercase">{tile.title}</div>
          <div className="font-mono text-[9px] text-white/40 mt-0.5">{tile.ward} · {tile.city}</div>
        </div>
        <button onClick={onClose} className="font-mono text-[9px] text-white/30 hover:text-white border border-white/15 px-2 py-1 transition-colors">✕</button>
      </div>

      {/* Section tabs */}
      <div className="flex gap-0.5">
        {sections.map((s, i) => (
          <button key={i} onClick={() => setSection(i)} title={s}
            className={`flex-1 h-1 transition-colors ${i <= section ? 'bg-white' : 'bg-white/15'}`} />
        ))}
      </div>
      <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest">{sections[section]}</div>

      {/* ZAHIR */}
      {section === 0 && (
        <div className="space-y-2">
          <div className="border-l-2 border-white/20 pl-3 py-1">
            <p className="font-mono text-[9px] text-white/50 italic leading-relaxed">&ldquo;{tile.ayah}&rdquo;</p>
          </div>
          <p className="font-mono text-xs text-white/60 leading-relaxed">{tile.desc}</p>
        </div>
      )}

      {/* THREE QUESTIONS */}
      {section === 1 && (
        <div className="space-y-2">
          <div className="border border-white/10 p-3">
            <div className="font-mono text-[9px] text-white uppercase tracking-wider mb-1">حدوث ذاتي — IS IT?</div>
            <p className="font-mono text-[9px] text-white/50 leading-relaxed">
              Yes — this exists as a biological creation, an Ayah of{' '}
              {tile.names.map(nid => {
                const nameKey = Object.keys(ALL_NAMES).find(k =>
                  k.toLowerCase().replace(/[^a-z]/g, '').includes(nid.replace(/[^a-z]/g, '')) ||
                  nid === k.toLowerCase().replace('al-', '').replace('ar-', '').replace('ash-', '')
                );
                const n = nameKey ? ALL_NAMES[nameKey] : null;
                return n ? n.en : nid;
              }).filter(Boolean).join(', ')}, and an Amanah placed in this region.
            </p>
          </div>
          <div className="border border-white/10 p-3">
            <div className="font-mono text-[9px] text-white uppercase tracking-wider mb-1">حدوث صفاتي — WHAT IS IT?</div>
            <p className="font-mono text-[9px] text-white/50 leading-relaxed">{tile.kkh_sl}</p>
          </div>
          <div className="border border-white/10 p-3">
            <div className="font-mono text-[9px] text-white uppercase tracking-wider mb-1">حدوث زماني — WHEN IS/WAS IT?</div>
            <p className="font-mono text-[9px] text-white/50 leading-relaxed">{tile.kkh_o}</p>
          </div>
          <TemporalSlider currentIdx={5} />
        </div>
      )}

      {/* SUNNAT ALLAH */}
      {section === 2 && (
        <div className="space-y-2">
          <div className="border-l-2 border-white/20 pl-3 py-1">
            <p className="font-mono text-[9px] text-white/50 italic leading-relaxed">&ldquo;{tile.ayah}&rdquo;</p>
          </div>
          <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-1">Names and Attributes extracted through the IRL Lens:</div>
          {tile.names.map(nid => {
            const nameKey = Object.keys(ALL_NAMES).find(k =>
              k.toLowerCase().replace(/[^a-z]/g, '').includes(nid.replace(/[^a-z]/g, '')) ||
              nid.toLowerCase().includes(k)
            );
            const n = nameKey ? ALL_NAMES[nameKey] : null;
            if (!n) return null;
            return (
              <div key={nid} className="border border-white/10 p-2 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-serif text-base text-white/80">{n.ar}</span>
                  <span className="font-mono text-[9px] text-white/60">{n.en} — {n.m}</span>
                </div>
                {'sim' in n && n.sim && (
                  <div className="font-mono text-[9px] text-white/50 mt-1 italic">{n.sim}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ED / DESIGN */}
      {section === 3 && (
        <div className="border border-white/10 p-3">
          <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">ED — AYAT IN CREATION — آيات في الخلق</div>
          <p className="font-mono text-xs text-white/60 leading-relaxed">{tile.kkh_ed}</p>
        </div>
      )}

      {/* AMANAH + BRIDGES */}
      {section === 4 && (
        <div className="space-y-3">
          <div className="border border-white/10 p-3">
            <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">أمانة — AMANAH — THE TRUST</div>
            <p className="font-mono text-xs text-white/60 leading-relaxed">{tile.kkh_a}</p>
          </div>
          {tile.bridges && tile.bridges.length > 0 && (
            <div className="border border-white/10 p-3">
              <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">BRIDGE LINKS — CONNECTED HAALS</div>
              {tile.bridges.map((bid, i) => (
                <button
                  key={bid}
                  onClick={() => onBridgeClick(bid)}
                  className="block w-full text-left px-3 py-2 mb-1 border border-white/10 hover:border-white/30 transition-colors"
                >
                  <span className="font-mono text-[9px] text-white/50">
                    {tile.bridge_labels?.[i] || bid}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between items-center pt-1">
        <button onClick={() => setSection(Math.max(0, section - 1))} disabled={section === 0}
          className="font-mono text-[9px] uppercase tracking-wider px-3 py-1.5 border border-white/15 text-white/30 hover:text-white hover:border-white disabled:opacity-20 transition-colors">← Prev</button>
        <span className="font-mono text-[9px] text-white/20">{section + 1} / {sections.length}</span>
        <button onClick={() => setSection(Math.min(sections.length - 1, section + 1))} disabled={section === sections.length - 1}
          className="font-mono text-[9px] uppercase tracking-wider px-3 py-1.5 border border-white/15 text-white/30 hover:text-white hover:border-white disabled:opacity-20 transition-colors">Next →</button>
      </div>
    </div>
  );
}

// ─── AYAT BRIDGE PANEL ────────────────────────────────────────────────────────────────

function AyatBridgePanel({ nameKey, onClose }: { nameKey: string; onClose: () => void }) {
  const ab = ALL_NAMES[nameKey];
  const [ayahIdx, setAyahIdx] = useState(0);
  if (!ab) return null;

  // Tiles manifesting this Name — search ECO_TILES + WARD_IRL
  const ecoTilesWithName = ECO_TILES.filter(t => t.names.some(nid =>
    nid === nameKey.toLowerCase().replace('al-', '').replace('ar-', '').replace('ash-', '') ||
    nameKey.toLowerCase().includes(nid)
  ));

  // Group eco tiles by city
  const ecoByCity: Record<string, EcoTile[]> = {};
  ecoTilesWithName.forEach(t => {
    if (!ecoByCity[t.city]) ecoByCity[t.city] = [];
    ecoByCity[t.city].push(t);
  });

  const processes = 'processes' in ab ? (ab as { processes: string[] }).processes :
    'procs' in ab ? (ab as { procs: string[] }).procs : [];
  const encounters = 'encounters' in ab ? (ab as { encounters: string[] }).encounters : [];

  return (
    <div className="space-y-4">
      <button onClick={onClose} className="font-mono text-[9px] uppercase tracking-widest text-white/40 hover:text-white border border-white/15 px-3 py-1.5 transition-colors">
        ← Back to Lens
      </button>
      <div className="border border-white/20 p-4 bg-white/5">
        <div className="font-serif text-3xl text-white mb-1">{ab.ar}</div>
        <div className="font-mono text-sm font-black text-white uppercase tracking-widest">{ab.en}</div>
        <div className="font-mono text-[10px] text-white/40 mt-1">Root: <span className="text-white/60">{ab.root}</span> — &ldquo;{ab.rootM}&rdquo;</div>
        {'m' in ab && <div className="font-mono text-[9px] text-white/50 mt-1">{(ab as { m: string }).m}</div>}
      </div>
      <div>
        <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">QURANIC AYAT</div>
        <div className="space-y-2">
          {ab.ayat.map((a, i) => (
            <button key={i} onClick={() => setAyahIdx(i)} className={`w-full text-left border p-3 transition-colors ${ayahIdx === i ? 'border-white/40 bg-white/5' : 'border-white/10 hover:border-white/25'}`}>
              <p className="font-mono text-xs text-white/60 leading-relaxed italic">&ldquo;{a}&rdquo;</p>
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">SUNNAT ALLAH — DIVINE PATTERNS</div>
        <div className="flex flex-wrap gap-1">
          {processes.map((pr, i) => (
            <span key={i} className="font-mono text-[9px] text-white/50 border border-white/10 px-2 py-1">{pr}</span>
          ))}
        </div>
      </div>
      {'sim' in ab && (ab as { sim: string }).sim && (
        <div className="border border-white/10 p-3 bg-white/3">
          <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-1">SIMILARITIES ACROSS CREATION</div>
          <p className="font-mono text-[9px] text-white/50 leading-relaxed italic">{(ab as { sim: string }).sim}</p>
        </div>
      )}
      {'field' in ab && (
        <div className="border border-white/20 p-4 bg-white/5">
          <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">FIELD PROMPT — APPLY THIS LENS</div>
          <p className="font-mono text-xs text-white/60 leading-relaxed italic">{(ab as { field: string }).field}</p>
        </div>
      )}
      {encounters.length > 0 && (
        <div>
          <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">WARD ENCOUNTERS WHERE {nameKey.toUpperCase()} MANIFESTS</div>
          <div className="space-y-2">
            {encounters.map((eid, i) => {
              let found: { enc: WardEncounter; city: string; ward: string } | null = null;
              Object.entries(WARD_IRL).forEach(([cid, wards]) => {
                Object.entries(wards).forEach(([wid, encs]) => {
                  const match = encs.find(e => e.id === eid);
                  if (match) found = { enc: match, city: cid, ward: wid };
                });
              });
              if (!found) return null;
              const f = found as { enc: WardEncounter; city: string; ward: string };
              return (
                <div key={i} className="border border-white/10 p-3">
                  <div className="font-mono text-xs font-black text-white">{f.enc.icon} {f.enc.title}</div>
                  <div className="font-mono text-[9px] text-white/40 mt-0.5 capitalize">{f.city} — {f.ward.replace(/-/g, ' ')}</div>
                  <div className="font-mono text-[9px] text-white/30 mt-1 leading-relaxed">{f.enc.zahir.desc.substring(0, 120)}...</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* TILES MANIFESTING THIS NAME — ECO tiles grouped by city */}
      {Object.keys(ecoByCity).length > 0 && (
        <div>
          <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">TILES MANIFESTING THIS NAME — AYAT IN CREATION</div>
          {Object.entries(ecoByCity).map(([city, tiles]) => (
            <div key={city} className="mb-3">
              <div className="font-mono text-[8px] text-white/20 uppercase tracking-widest mb-1">{city}</div>
              {tiles.map(t => (
                <div key={t.id} className="border border-white/10 p-2 mb-1" style={{ borderLeftColor: t.cat === 'Creation' ? '#2d7a47' : '#1a6b4e', borderLeftWidth: 2 }}>
                  <div className="font-mono text-[9px] font-black text-white">{t.title}</div>
                  <div className="font-mono text-[8px] text-white/30 mt-0.5">{t.ward} · {t.cat}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SUBMISSION FORM ────────────────────────────────────────────────────────────────────────

function SubmissionForm({ wardId, wardName, cityId, onCancel }: { wardId: string; wardName: string; cityId: string; onCancel: () => void }) {
  const [formType, setFormType] = useState<'observation' | 'resource'>('observation');
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [isQ, setIsQ] = useState('');
  const [whatQ, setWhatQ] = useState('');
  const [whenQ, setWhenQ] = useState('');
  const [cat, setCat] = useState('Food');
  const [addr, setAddr] = useState('');
  const [names, setNames] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const allNames = Object.keys(AYAT_BRIDGE);
  const categories = ['Food', 'Health', 'Education', 'Income', 'Housing', 'Reentry', 'Justice', 'Community'];
  const toggleName = (n: string) => setNames(prev => prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n]);
  const handleSubmit = () => {
    const sub: CaretakerSubmission = { id: `sub_${Date.now()}`, type: formType, cityId, wardId, wardName, title, description: desc, category: formType === 'resource' ? cat : undefined, address: formType === 'resource' ? addr : undefined, threeQuestions: formType === 'observation' ? { is: isQ, what: whatQ, when: whenQ } : undefined, namesAttributes: names, status: 'pending', createdAt: new Date().toISOString() };
    saveSubmission(sub);
    setSubmitted(true);
  };
  const reset = () => { setSubmitted(false); setTitle(''); setDesc(''); setIsQ(''); setWhatQ(''); setWhenQ(''); setNames([]); };
  if (submitted) return (
    <div className="border border-white/20 p-6 text-center space-y-4">
      <div className="font-serif text-2xl text-white">الحمد لله</div>
      <div className="font-mono text-sm font-black text-white uppercase tracking-widest">Alhamdulillah</div>
      <p className="font-mono text-xs text-white/50 leading-relaxed">Your {formType} has been submitted for review. When approved, it will be added to the {wardName} ward catalog.</p>
      <div className="border border-white/10 p-3"><p className="font-mono text-[9px] text-white/40 italic">&ldquo;We have not neglected in the Register a thing.&rdquo; (6:38)</p></div>
      <div className="flex gap-2 justify-center">
        <button onClick={reset} className="font-mono text-[9px] uppercase tracking-widest px-4 py-2 border border-white/30 text-white/60 hover:text-white hover:border-white transition-colors">Submit Another</button>
        <button onClick={onCancel} className="font-mono text-[9px] uppercase tracking-widest px-4 py-2 border border-white/15 text-white/40 hover:text-white/60 transition-colors">Return to Lens</button>
      </div>
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest">CARETAKER SUBMISSION — {wardName.toUpperCase()}</div>
      <div className="flex gap-px border border-white/20">
        {([{ id: 'observation' as const, l: 'IRL Observation', d: 'Apply the Three Questions to something you encountered' }, { id: 'resource' as const, l: 'Resource Found', d: 'Submit a resource for the community catalog' }]).map(ft => (
          <button key={ft.id} onClick={() => setFormType(ft.id)} className={`flex-1 p-3 text-left transition-colors ${formType === ft.id ? 'bg-white/10' : 'hover:bg-white/5'}`}>
            <div className={`font-mono text-[9px] font-black uppercase tracking-widest ${formType === ft.id ? 'text-white' : 'text-white/40'}`}>{ft.l}</div>
            <div className="font-mono text-[9px] text-white/30 mt-0.5">{ft.d}</div>
          </button>
        ))}
      </div>
      <div>
        <label className="font-mono text-[9px] text-white/40 uppercase tracking-widest block mb-1">TITLE</label>
        <input value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-black border border-white/20 text-white font-mono text-xs px-3 py-2 outline-none focus:border-white/50 placeholder-white/20" placeholder={formType === 'observation' ? 'e.g. Vacant lot with emerging prairie' : 'e.g. New halal market'} />
      </div>
      <div>
        <label className="font-mono text-[9px] text-white/40 uppercase tracking-widest block mb-1">DESCRIPTION — Zahir observations</label>
        <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={3} className="w-full bg-black border border-white/20 text-white font-mono text-xs px-3 py-2 outline-none focus:border-white/50 placeholder-white/20 resize-none" placeholder="What did you see, hear, notice? Describe the Zahir." />
      </div>
      {formType === 'observation' && (
        <div className="border border-white/10 p-3 space-y-3">
          <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest">The Three Questions — Huduth</div>
          {[{ label: 'IS IT? — Huduth Dhati', val: isQ, set: setIsQ, ph: 'Does this thing exist? What is its state of being?' }, { label: 'WHAT IS IT? — Huduth Sifati', val: whatQ, set: setWhatQ, ph: 'What are its qualities? What Names compose this?' }, { label: 'WHEN IS/WAS IT? — Huduth Zamani', val: whenQ, set: setWhenQ, ph: 'When was this originated? What is its timeline?' }].map(({ label, val, set, ph }) => (
            <div key={label}>
              <label className="font-mono text-[9px] text-white/40 uppercase tracking-widest block mb-1">{label}</label>
              <textarea value={val} onChange={e => set(e.target.value)} rows={2} className="w-full bg-black border border-white/15 text-white font-mono text-xs px-3 py-2 outline-none focus:border-white/40 placeholder-white/15 resize-none" placeholder={ph} />
            </div>
          ))}
        </div>
      )}
      {formType === 'resource' && (
        <div className="space-y-3">
          <div>
            <label className="font-mono text-[9px] text-white/40 uppercase tracking-widest block mb-1">CATEGORY</label>
            <div className="flex flex-wrap gap-1">{categories.map(c => (<button key={c} onClick={() => setCat(c)} className={`font-mono text-[9px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${cat === c ? 'bg-white text-black border-white' : 'text-white/40 border-white/15 hover:border-white/40'}`}>{c}</button>))}</div>
          </div>
          <div>
            <label className="font-mono text-[9px] text-white/40 uppercase tracking-widest block mb-1">ADDRESS / LOCATION</label>
            <input value={addr} onChange={e => setAddr(e.target.value)} className="w-full bg-black border border-white/20 text-white font-mono text-xs px-3 py-2 outline-none focus:border-white/50 placeholder-white/20" placeholder="Street address or cross streets" />
          </div>
        </div>
      )}
      <div>
        <label className="font-mono text-[9px] text-white/40 uppercase tracking-widest block mb-1">NAMES / ATTRIBUTES RECOGNIZED</label>
        <p className="font-mono text-[9px] text-white/25 mb-2">Which Names of Allah do you recognize manifesting in this encounter?</p>
        <div className="flex flex-wrap gap-1">
          {allNames.map(n => { const ab = AYAT_BRIDGE[n]; const sel = names.includes(n); return (<button key={n} onClick={() => toggleName(n)} className={`font-mono text-[9px] px-2 py-1 border transition-colors ${sel ? 'bg-white text-black border-white' : 'text-white/40 border-white/15 hover:border-white/40'}`}>{ab.ar} {ab.en}</button>); })}
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={handleSubmit} disabled={!title} className="font-mono text-[9px] uppercase tracking-widest px-5 py-2.5 bg-white text-black hover:bg-white/90 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">Submit for Review</button>
        <button onClick={onCancel} className="font-mono text-[9px] uppercase tracking-widest px-4 py-2.5 border border-white/20 text-white/40 hover:text-white/70 transition-colors">Cancel</button>
      </div>
      <p className="font-mono text-[9px] text-white/20 leading-relaxed">Submissions are reviewed by senior Caretakers before being added to the catalog.</p>
    </div>
  );
}

// ─── CATALOG VIEW ─────────────────────────────────────────────────────────────────────────

function CatalogView({ wardId, wardName, cityId }: { wardId: string; wardName: string; cityId: string }) {
  const [subs, setSubs] = useState<CaretakerSubmission[]>([]);
  useEffect(() => { setSubs(getSubmissions().filter(s => s.cityId === cityId && s.wardId === wardId)); }, [cityId, wardId]);
  if (subs.length === 0) return (
    <div className="border border-white/10 p-6 text-center space-y-3">
      <div className="font-mono text-xs text-white/30">No submissions yet for {wardName}</div>
      <p className="font-mono text-[9px] text-white/20 leading-relaxed">Be the first Caretaker to document an observation or resource in this ward.</p>
      <div className="border border-white/10 p-3"><p className="font-mono text-[9px] text-white/30 italic">&ldquo;We have not neglected in the Register a thing.&rdquo; (6:38)</p></div>
    </div>
  );
  return (
    <div className="space-y-3">
      <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest">CARETAKER CATALOG — {wardName.toUpperCase()} ({subs.length} SUBMISSION{subs.length !== 1 ? 'S' : ''})</div>
      {subs.map((s, i) => (
        <div key={s.id || i} className={`border p-3 ${s.type === 'observation' ? 'border-white/20' : 'border-white/15'}`}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="font-mono text-xs font-black text-white">{s.title}</div>
            <span className="font-mono text-[8px] text-white/30 border border-white/15 px-1.5 py-0.5 flex-shrink-0">{s.status.toUpperCase()}</span>
          </div>
          <div className="font-mono text-[9px] text-white/40 mb-2">{s.type === 'observation' ? 'IRL Observation' : 'Resource'} · {new Date(s.createdAt).toLocaleDateString()}</div>
          {s.description && <p className="font-mono text-[9px] text-white/50 leading-relaxed mb-2">{s.description.substring(0, 200)}{s.description.length > 200 ? '...' : ''}</p>}
          {s.namesAttributes && s.namesAttributes.length > 0 && (
            <div className="flex flex-wrap gap-1">{s.namesAttributes.map(n => { const ab = AYAT_BRIDGE[n]; return (<span key={n} className="font-mono text-[8px] text-white/40 border border-white/10 px-1.5 py-0.5">{ab ? `${ab.ar} ` : ''}{n}</span>); })}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── ENCOUNTER CARD ─────────────────────────────────────────────────────────────────────────

function EncounterCard({ enc, onNameClick }: { enc: WardEncounter; onNameClick: (name: string) => void }) {
  const [step, setStep] = useState(0);
  const steps = ['ZAHIR', 'THREE QUESTIONS', 'SUNNAT ALLAH', 'HAQQ', 'AMANAH', 'UBOOR', 'CATALOG'];
  return (
    <div className="border border-white/15 p-4 space-y-3">
      <div className="flex gap-0.5">{steps.map((s, i) => (<button key={i} onClick={() => setStep(i)} title={s} className={`flex-1 h-1 transition-colors ${i <= step ? 'bg-white' : 'bg-white/15'}`} />))}</div>
      <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest">{steps[step]}</div>
      <div className="flex items-center gap-2">
        <span className="text-xl">{enc.icon}</span>
        <div className="font-mono text-xs font-black text-white uppercase">{enc.title}</div>
      </div>
      {step === 0 && (
        <div>
          <p className="font-mono text-xs text-white/60 leading-relaxed mb-3">{enc.zahir.desc}</p>
          <div className="space-y-1">{enc.zahir.obs.map((o, i) => (<div key={i} className="font-mono text-[9px] text-white/40 flex gap-2"><span className="text-white/20 flex-shrink-0">*</span><span>{o}</span></div>))}</div>
        </div>
      )}
      {step === 1 && (
        <div className="space-y-2">
          {[{ q: 'IS IT? (Wujud)', a: enc.threeQ.is }, { q: 'WHAT IS IT? (Mahiyya)', a: enc.threeQ.what }, { q: 'WHEN IS/WAS IT? (Huduth Zamani)', a: enc.threeQ.when }].map((item, i) => (
            <div key={i} className="border border-white/10 p-3">
              <div className="font-mono text-[9px] text-white uppercase tracking-wider mb-1">{item.q}</div>
              <p className="font-mono text-[9px] text-white/50 leading-relaxed">{item.a}</p>
            </div>
          ))}
          <TemporalSlider currentIdx={5} />
        </div>
      )}
      {step === 2 && (
        <div className="space-y-3">
          {enc.sunnat.map((s, si) => (
            <div key={si}>
              <div className="font-mono text-[9px] text-white font-black uppercase mb-1">{s.process}</div>
              <div className="font-mono text-[9px] text-white/40 mb-2 italic">{s.sci}</div>
              <div className="space-y-2">
                {s.attrs.map((a, ai) => {
                  const hasAyat = !!AYAT_BRIDGE[a.en] || !!EXTENDED_NAMES[a.en];
                  const nameData = AYAT_BRIDGE[a.en] || EXTENDED_NAMES[a.en];
                  return (
                    <div key={ai} className="border border-white/10 p-2">
                      <button onClick={hasAyat ? () => onNameClick(a.en) : undefined} className={`font-mono text-xs text-white/80 ${hasAyat ? 'hover:text-white cursor-pointer underline decoration-white/20' : ''}`}>
                        {a.ar} — {a.en} — {a.m}{hasAyat ? ' →' : ''}
                      </button>
                      <p className="font-mono text-[9px] text-white/40 mt-1">{a.role}</p>
                      {'sim' in (nameData || {}) && (nameData as { sim?: string }).sim && (
                        <div className="mt-1 border border-white/5 p-1.5 bg-white/3">
                          <div className="font-mono text-[8px] text-white/20 uppercase tracking-widest mb-0.5">Also manifests in:</div>
                          <p className="font-mono text-[8px] text-white/35 italic leading-relaxed">{(nameData as { sim: string }).sim}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="font-mono text-[9px] text-white/30 italic mt-2">{s.ayah}</div>
            </div>
          ))}
        </div>
      )}
      {step === 3 && (<div className="border border-white/15 p-3 bg-white/3"><div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">الحق — Haqq: Truth, Right, Reality, Purpose</div><p className="font-mono text-xs text-white/60 leading-relaxed">{enc.haqq}</p></div>)}
      {step === 4 && (<div className="border border-white/15 p-3"><div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">Amanah: The Trust</div><p className="font-mono text-xs text-white/60 leading-relaxed">{enc.amanah}</p></div>)}
      {step === 5 && (
        <div>
          <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">Uboor: The Bridge</div>
          <p className="font-mono text-xs text-white/60 leading-relaxed mb-3">{enc.uboor}</p>
          <div className="flex flex-wrap gap-1">{enc.bridges.map(b => (<span key={b} className="font-mono text-[9px] text-white border border-white/30 px-2 py-1 uppercase">{b}</span>))}</div>
        </div>
      )}
      {step === 6 && (<div className="border border-white/10 p-3 bg-white/3"><div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">Catalog — The Living Network</div><p className="font-mono text-xs text-white/50 leading-relaxed italic">{enc.catalog}</p><div className="font-mono text-[9px] text-white/20 mt-2">&ldquo;We have not neglected in the Register a thing.&rdquo; (6:38)</div></div>)}
      <div className="flex justify-between items-center pt-1">
        <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="font-mono text-[9px] uppercase tracking-wider px-3 py-1.5 border border-white/15 text-white/30 hover:text-white hover:border-white disabled:opacity-20 transition-colors">← Prev</button>
        <span className="font-mono text-[9px] text-white/20">{step + 1} / {steps.length}</span>
        <button onClick={() => setStep(Math.min(steps.length - 1, step + 1))} disabled={step === steps.length - 1} className="font-mono text-[9px] uppercase tracking-wider px-3 py-1.5 border border-white/15 text-white/30 hover:text-white hover:border-white disabled:opacity-20 transition-colors">Next →</button>
      </div>
    </div>
  );
}

// ─── CROSS-CITY ENCOUNTERS BROWSER ────────────────────────────────────────────────────────

function CrossCityBrowser({ onNameClick }: { onNameClick: (name: string) => void }) {
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [selectedWard, setSelectedWard] = useState<string | null>(null);

  const cities = Object.keys(WARD_IRL);
  const wards = selectedCity ? Object.keys(WARD_IRL[selectedCity] || {}) : [];
  const encounters = selectedCity && selectedWard ? (WARD_IRL[selectedCity]?.[selectedWard] ?? []) : [];

  const CITY_LABELS: Record<string, string> = {
    chicago: 'Chicago', atlanta: 'Atlanta', cincinnati: 'Cincinnati', detroit: 'Detroit',
    louisville: 'Louisville', durham: 'Durham', portland: 'Portland', pittsburgh: 'Pittsburgh',
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">SELECT CITY</div>
        <div className="flex flex-wrap gap-1">
          {cities.map(c => (
            <button key={c} onClick={() => { setSelectedCity(selectedCity === c ? null : c); setSelectedWard(null); }}
              className={`font-mono text-[9px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${
                selectedCity === c ? 'bg-white text-black border-white' : 'text-white/40 border-white/15 hover:border-white/40'
              }`}>{CITY_LABELS[c] ?? c}</button>
          ))}
        </div>
      </div>
      {selectedCity && (
        <div>
          <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">SELECT WARD — {(CITY_LABELS[selectedCity] ?? selectedCity).toUpperCase()}</div>
          <div className="flex flex-wrap gap-1">
            {wards.map(w => (
              <button key={w} onClick={() => setSelectedWard(selectedWard === w ? null : w)}
                className={`font-mono text-[9px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${
                  selectedWard === w ? 'bg-white text-black border-white' : 'text-white/40 border-white/15 hover:border-white/40'
                }`}>{w.replace(/-/g, ' ')}</button>
            ))}
          </div>
        </div>
      )}
      {encounters.length > 0 && (
        <div className="space-y-3">
          <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest">
            {(CITY_LABELS[selectedCity!] ?? selectedCity).toUpperCase()} — {selectedWard!.replace(/-/g, ' ').toUpperCase()} ({encounters.length} ENCOUNTER{encounters.length !== 1 ? 'S' : ''})
          </div>
          {encounters.map(enc => (<EncounterCard key={enc.id} enc={enc} onNameClick={onNameClick} />))}
        </div>
      )}
      {selectedCity && selectedWard && encounters.length === 0 && (
        <div className="border border-white/10 p-4 text-center">
          <div className="font-mono text-xs text-white/30">No encounters cataloged for this ward yet</div>
        </div>
      )}
      {!selectedCity && (
        <div className="border border-white/10 p-4 bg-white/3">
          <p className="font-mono text-[9px] text-white/30 leading-relaxed">
            Browse IRL encounters across all 8 cities in the Caretaker network. Select a city above to begin.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── CONTEXT BRIDGE PANEL ─────────────────────────────────────────────────────────────────

function ContextBridgePanel({ cityId, wardId, wardName }: { cityId: string; wardId: string; wardName: string }) {
  const ctxText = CTX_BRIDGE[cityId]?.[wardId];
  const wardEncs = WARD_IRL[cityId]?.[wardId] ?? [];

  if (!ctxText && wardEncs.length === 0) {
    return (
      <div className="border border-white/10 p-4 text-center">
        <div className="font-mono text-xs text-white/30 mb-2">No context bridge entry for {wardName}</div>
        <p className="font-mono text-[9px] text-white/20 leading-relaxed">Context bridges connect historical sites to IRL Lens encounters. This ward&apos;s bridge is being built by the Caretaker network.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest">CONTEXT ↔ IRL LENS BRIDGE — {wardName.toUpperCase()}</div>
      {ctxText && (
        <div className="border border-white/20 p-4 bg-white/5">
          <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">HUDUTH ZAMANI — TEMPORAL CONTEXT</div>
          <p className="font-mono text-xs text-white/60 leading-relaxed">{ctxText}</p>
        </div>
      )}
      {wardEncs.length > 0 && (
        <div>
          <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">IRL ENCOUNTERS IN THIS WARD ({wardEncs.length})</div>
          <div className="space-y-2">
            {wardEncs.map((enc, i) => (
              <div key={i} className="border border-white/10 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{enc.icon}</span>
                  <div className="font-mono text-xs font-black text-white uppercase">{enc.title}</div>
                </div>
                <p className="font-mono text-[9px] text-white/40 leading-relaxed mb-2">{enc.zahir.desc.substring(0, 150)}...</p>
                <div className="flex flex-wrap gap-1">
                  {enc.bridges.map(b => (<span key={b} className="font-mono text-[8px] text-white/50 border border-white/15 px-1.5 py-0.5 uppercase">{b}</span>))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="border border-white/10 p-3 bg-white/3">
        <p className="font-mono text-[9px] text-white/30 leading-relaxed italic">
          &ldquo;Explore and reflect on the knowledge of the states of our communities that are underserved.&rdquo;
        </p>
        <p className="font-mono text-[9px] text-white/20 mt-1">Open Letter to Imprisoned Caretakers</p>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────────────────────

export default function IRLLensExpanded({ wardId, wardName, cityId, foodDesertPct }: IRLLensExpandedProps) {
  const [mode, setMode] = useState<'encounters' | 'network' | 'seasonal' | 'deep' | 'submit' | 'ayat' | 'catalog' | 'bridge' | 'ayat-creation'>('encounters');
  const [deepTab, setDeepTab] = useState<'fission' | 'psychological' | 'stem' | null>(null);
  const [ayatName, setAyatName] = useState<string | null>(null);
  const [ecoFilter, setEcoFilter] = useState<'all' | 'comm' | 'eco'>('all');
  const [selectedEcoTile, setSelectedEcoTile] = useState<EcoTile | null>(null);
  const season = getSeason();
  const sData = SEASONAL[season];
  const wardEncs: WardEncounter[] = WARD_IRL[cityId]?.[wardId] ?? [];
  const hasCtxBridge = !!(CTX_BRIDGE[cityId]?.[wardId]);

  // Capitalize city for BIOMES lookup
  const cityCapitalized = cityId.charAt(0).toUpperCase() + cityId.slice(1);
  const biome = BIOMES[cityCapitalized];
  const cityEcoTiles = ECO_TILES.filter(t => t.city.toLowerCase() === cityId.toLowerCase());

  const handleNameClick = (name: string) => { setAyatName(name); setMode('ayat'); };

  const handleBridgeClick = (tileId: string) => {
    const target = ECO_TILES.find(t => t.id === tileId);
    if (target) {
      setSelectedEcoTile(target);
      setMode('ayat-creation');
    }
  };

  const modeTabs = [
    { id: 'encounters' as const, label: 'Encounters' },
    { id: 'network' as const, label: '🌐 Network' },
    { id: 'seasonal' as const, label: `${sData.icon} ${sData.name}` },
    { id: 'deep' as const, label: 'Deep Dive' },
    { id: 'submit' as const, label: '+ Submit' },
    { id: 'ayat' as const, label: 'Ayat Bridge' },
    { id: 'catalog' as const, label: 'Catalog' },
    { id: 'ayat-creation' as const, label: '🌿 Ayat in Creation' },
    ...(hasCtxBridge ? [{ id: 'bridge' as const, label: 'Ctx Bridge' }] : []),
  ];

  return (
    <div>
      <div className="flex flex-wrap gap-1 mb-4 border-b border-white/10 pb-3">
        {modeTabs.map(t => (
          <button key={t.id} onClick={() => { setMode(t.id); if (t.id !== 'ayat') setAyatName(null); if (t.id !== 'ayat-creation') setSelectedEcoTile(null); }}
            className={`font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${mode === t.id ? 'bg-white text-black border-white' : 'text-white/30 border-white/15 hover:text-white/60'}`}>{t.label}</button>
        ))}
      </div>

      {/* WARD ENCOUNTERS */}
      {mode === 'encounters' && (
        <div className="space-y-4">
          {/* ECO/COMM filter */}
          <div className="flex gap-1">
            {([{ id: 'all' as const, label: `ALL (${wardEncs.length})` }, { id: 'comm' as const, label: `COMMUNITY (${wardEncs.length})` }, { id: 'eco' as const, label: `AYAT IN CREATION (${cityEcoTiles.length})` }]).map(f => (
              <button key={f.id} onClick={() => setEcoFilter(f.id)}
                className={`font-mono text-[8px] uppercase tracking-wider px-2 py-1 border transition-colors ${
                  ecoFilter === f.id ? 'bg-white text-black border-white' : 'text-white/30 border-white/15 hover:border-white/40'
                }`}>{f.label}</button>
            ))}
          </div>
          {(ecoFilter === 'all' || ecoFilter === 'comm') && wardEncs.length > 0 && (
            <>
              <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest">{wardName.toUpperCase()} — WARD-SPECIFIC ENCOUNTERS ({wardEncs.length})</div>
              {wardEncs.map(enc => (<EncounterCard key={enc.id} enc={enc} onNameClick={handleNameClick} />))}
            </>
          )}
          {(ecoFilter === 'all' || ecoFilter === 'eco') && cityEcoTiles.length > 0 && (
            <>
              <div className="font-mono text-[9px] uppercase tracking-widest mt-2" style={{ color: '#2d7a47' }}>
                {biome ? `${biome.name} — ` : ''}{cityCapitalized.toUpperCase()} AYAT IN CREATION ({cityEcoTiles.length})
              </div>
              <div className="grid grid-cols-1 gap-2">
                {cityEcoTiles.map(tile => (
                  <button key={tile.id} onClick={() => { setSelectedEcoTile(tile); setMode('ayat-creation'); }}
                    className="border border-white/10 p-3 text-left hover:border-white/30 transition-colors"
                    style={{ borderLeftColor: tile.cat === 'Creation' ? '#2d7a47' : '#1a6b4e', borderLeftWidth: 2 }}>
                    <div className="font-mono text-xs font-black text-white">{tile.title}</div>
                    <div className="font-mono text-[9px] text-white/40 mt-0.5">{tile.ward} · <span style={{ color: tile.cat === 'Creation' ? '#2d7a47' : '#1a6b4e' }}>{tile.cat}</span></div>
                  </button>
                ))}
              </div>
            </>
          )}
          {ecoFilter === 'comm' && wardEncs.length === 0 && (
            <div className="border border-white/10 p-4 text-center">
              <div className="font-mono text-xs text-white/30 mb-2">No ward-specific encounters cataloged yet</div>
            </div>
          )}
          {foodDesertPct > 0 && (
            <div className="border border-white/10 p-3 bg-white/3">
              <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-1">WARD CONTEXT — AMANAH</div>
              <p className="font-mono text-[9px] text-white/50 leading-relaxed">{foodDesertPct}% food desert. The Rizq exists in the earth — the Amanah is to trace where the barriers are and bridge to Islah.</p>
            </div>
          )}
        </div>
      )}

      {/* NETWORK — CROSS-CITY BROWSER */}
      {mode === 'network' && (
        <div className="space-y-4">
          <div>
            <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-1">CARETAKER NETWORK — 8 CITIES · CROSS-CITY ENCOUNTERS</div>
            <p className="font-mono text-[9px] text-white/40 leading-relaxed">
              The Caretaker network spans Chicago, Atlanta, Cincinnati, Detroit, Louisville, Durham, Portland, and Pittsburgh.
            </p>
          </div>
          <CrossCityBrowser onNameClick={handleNameClick} />
        </div>
      )}

      {/* SEASONAL */}
      {mode === 'seasonal' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{sData.icon}</span>
            <div>
              <div className="font-mono text-sm font-black text-white uppercase tracking-widest">{sData.name}</div>
              <div className="font-serif text-lg text-white/50">{sData.ar}</div>
            </div>
          </div>
          <p className="font-mono text-xs text-white/60 leading-relaxed">{sData.note}</p>
          <div>
            <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">DIVINE ATTRIBUTES OF THE SEASON</div>
            <div className="space-y-2">
              {sData.attrs.map((a, i) => (
                <div key={i} className="border border-white/10 p-3">
                  <div className="flex items-center gap-2 mb-1"><span className="font-serif text-base text-white/80">{a.ar}</span><span className="font-mono text-[9px] text-white/60">{a.en} — {a.m}</span></div>
                  <p className="font-mono text-[9px] text-white/40 leading-relaxed">{a.role}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="border border-white/10 p-3 bg-white/3"><p className="font-mono text-xs text-white/50 italic leading-relaxed">{sData.ayah}</p></div>
          <div>
            <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">WHAT TO OBSERVE IN {wardName.toUpperCase()} THIS {sData.name.toUpperCase()}</div>
            <div className="space-y-1">{sData.observe.map((o, i) => (<div key={i} className="font-mono text-[9px] text-white/50 flex gap-2"><span className="text-white/20 flex-shrink-0">*</span><span>{o}</span></div>))}</div>
          </div>
        </div>
      )}

      {/* DEEP DIVE */}
      {mode === 'deep' && (
        <div className="space-y-4">
          <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest">Optional Depth — For Study, Exploration and Analysis</div>
          <div className="flex flex-wrap gap-1">
            {([{ id: 'fission' as const, l: 'Fission Reactor' }, { id: 'psychological' as const, l: 'Hawas to Hikmah' }, { id: 'stem' as const, l: 'STEM as Sunnat Allah' }]).map(t => (
              <button key={t.id} onClick={() => setDeepTab(deepTab === t.id ? null : t.id)} className={`font-mono text-[9px] uppercase tracking-widest px-3 py-2 border transition-colors ${deepTab === t.id ? 'bg-white text-black border-white' : 'text-white/40 border-white/15 hover:border-white/40'}`}>{t.l}</button>
            ))}
          </div>
          {deepTab === 'fission' && (
            <div className="space-y-3">
              <div className="font-mono text-xs font-black text-white uppercase">{DEEP_DIVE.fission.title}</div>
              <p className="font-mono text-[9px] text-white/60 leading-relaxed">{DEEP_DIVE.fission.intro}</p>
              <div className="border border-white/10 p-3 bg-white/3"><div className="font-mono text-[9px] text-white/50 leading-relaxed">{DEEP_DIVE.fission.diagram}</div></div>
              <div className="space-y-2">{DEEP_DIVE.fission.components.map((c, i) => (<div key={i} className="border border-white/10 p-3"><div className="font-mono text-[9px] font-black text-white uppercase mb-1">{c.name}</div><p className="font-mono text-[9px] text-white/50 leading-relaxed">{c.desc}</p></div>))}</div>
              <div className="border border-white/10 p-3"><p className="font-mono text-[9px] text-white/40 italic">{DEEP_DIVE.fission.ayah}</p></div>
            </div>
          )}
          {deepTab === 'psychological' && (
            <div className="space-y-3">
              <div className="font-mono text-xs font-black text-white uppercase">{DEEP_DIVE.psychological.title}</div>
              <p className="font-mono text-[9px] text-white/60 leading-relaxed">{DEEP_DIVE.psychological.intro}</p>
              <div className="space-y-2">{DEEP_DIVE.psychological.stages.map((s, i) => (<div key={i} className="border border-white/10 p-3"><div className="font-mono text-[9px] font-black text-white uppercase mb-1">{s.name}</div><p className="font-mono text-[9px] text-white/50 leading-relaxed mb-2">{s.desc}</p><div className="flex items-center gap-2"><span className="font-serif text-sm text-white/60">{s.attr.ar}</span><span className="font-mono text-[9px] text-white/40">{s.attr.en} — {s.attr.m}</span></div><p className="font-mono text-[9px] text-white/30 mt-1 italic">{s.process}</p></div>))}</div>
              <div className="border border-white/10 p-3"><p className="font-mono text-[9px] text-white/40 italic">{DEEP_DIVE.psychological.ayah}</p></div>
            </div>
          )}
          {deepTab === 'stem' && (
            <div className="space-y-3">
              <div className="font-mono text-xs font-black text-white uppercase">{DEEP_DIVE.stem.title}</div>
              <p className="font-mono text-[9px] text-white/60 leading-relaxed">{DEEP_DIVE.stem.intro}</p>
              <div className="space-y-3">{DEEP_DIVE.stem.processes.map((pr, i) => (<div key={i} className="border border-white/10 p-3 space-y-2"><div className="font-mono text-[9px] font-black text-white uppercase">{pr.name}</div><div className="font-mono text-[9px] text-white/50 italic">{pr.sci}</div><p className="font-mono text-[9px] text-white/50 leading-relaxed">{pr.detail}</p><div className="flex flex-wrap gap-1">{pr.attrs.map((a, ai) => (<span key={ai} className="font-mono text-[8px] text-white/40 border border-white/10 px-1.5 py-0.5">{a}</span>))}</div><div className="font-mono text-[9px] text-white/30 italic">{pr.ayah}</div><div className="border border-white/10 p-2 bg-white/3"><div className="font-mono text-[8px] text-white/30 uppercase tracking-widest mb-1">FIELD OBSERVATION</div><p className="font-mono text-[9px] text-white/50 leading-relaxed">{pr.field}</p></div></div>))}</div>
            </div>
          )}
          {!deepTab && (<div className="border border-white/10 p-4 text-center"><p className="font-mono text-[9px] text-white/30 leading-relaxed">Select a depth layer above to explore the fission reactor framework, the psychological pathway from senses to wisdom, or STEM processes as Sunnat Allah.</p></div>)}
        </div>
      )}

      {/* SUBMIT */}
      {mode === 'submit' && (<SubmissionForm wardId={wardId} wardName={wardName} cityId={cityId} onCancel={() => setMode('encounters')} />)}

      {/* AYAT BRIDGE GRID */}
      {mode === 'ayat' && !ayatName && (
        <div className="space-y-4">
          <div>
            <div className="font-mono text-[9px] text-white/30 uppercase tracking-widest mb-2">Ayat Bridge — Names as Vectors</div>
            <p className="font-mono text-[9px] text-white/40 leading-relaxed">Every Name of Allah is a ray (ayah) pointing from creation back to the Creator. Tap a Name to explore where it manifests across all wards, its Quranic context, and how to apply this lens in your own environment.</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(ALL_NAMES).map(([nk, ab]) => {
              const processes = 'processes' in ab ? (ab as { processes: string[] }).processes :
                'procs' in ab ? (ab as { procs: string[] }).procs : [];
              const encounters = 'encounters' in ab ? (ab as { encounters: string[] }).encounters : [];
              return (
                <button key={nk} onClick={() => setAyatName(nk)} className="border border-white/15 p-3 text-left hover:border-white/40 hover:bg-white/3 transition-colors">
                  <div className="font-serif text-lg text-white/80 mb-0.5">{ab.ar}</div>
                  <div className="font-mono text-[9px] font-black text-white uppercase tracking-wider">{ab.en}</div>
                  <div className="font-mono text-[8px] text-white/30 mt-1">{encounters.length} encounter{encounters.length !== 1 ? 's' : ''} · {processes.length} processes</div>
                </button>
              );
            })}
          </div>
          <div className="border border-white/10 p-3 bg-white/3"><p className="font-mono text-[9px] text-white/30 leading-relaxed">This bridge connects the IRL Lens to the Ayat of Creation platform. Each Name is a gateway: from observable reality through divine recognition to understanding and service.</p></div>
        </div>
      )}

      {/* AYAT BRIDGE DETAIL */}
      {mode === 'ayat' && ayatName && (<AyatBridgePanel nameKey={ayatName} onClose={() => setAyatName(null)} />)}

      {/* CATALOG */}
      {mode === 'catalog' && (<CatalogView wardId={wardId} wardName={wardName} cityId={cityId} />)}

      {/* AYAT IN CREATION TAB */}
      {mode === 'ayat-creation' && (
        <div className="space-y-4">
          {selectedEcoTile ? (
            <EcoTileDetail
              tile={selectedEcoTile}
              onClose={() => setSelectedEcoTile(null)}
              onBridgeClick={handleBridgeClick}
            />
          ) : (
            <>
              <div>
                <div className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: '#2d7a47' }}>
                  🌿 AYAT IN CREATION — {cityCapitalized.toUpperCase()}
                </div>
                {biome && (
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: '#2d7a47' }} />
                    <span className="font-mono text-[9px] text-white/50">{biome.name}</span>
                    <span className="font-serif text-sm text-white/30">{biome.ar}</span>
                  </div>
                )}
                <p className="font-mono text-[9px] text-white/40 leading-relaxed">
                  Ecological and creation encounters from {cityCapitalized}&apos;s biome. Each tile is an Ayah — a sign pointing back to the Names of Allah through the natural world.
                </p>
              </div>
              {cityEcoTiles.length === 0 ? (
                <div className="border border-white/10 p-6 text-center">
                  <div className="font-mono text-xs text-white/30">No Ayat in Creation tiles for {cityCapitalized} yet</div>
                </div>
              ) : (
                <div className="space-y-2">
                  {cityEcoTiles.map(tile => (
                    <button key={tile.id} onClick={() => setSelectedEcoTile(tile)}
                      className="w-full border border-white/10 p-3 text-left hover:border-white/30 transition-colors"
                      style={{ borderLeftColor: tile.cat === 'Creation' ? '#2d7a47' : '#1a6b4e', borderLeftWidth: 3 }}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="font-mono text-xs font-black text-white">{tile.title}</div>
                          <div className="font-mono text-[9px] text-white/40 mt-0.5">{tile.ward}</div>
                          <div className="font-mono text-[9px] mt-1 leading-relaxed text-white/30 line-clamp-2">{tile.desc.substring(0, 100)}...</div>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <span className="font-mono text-[8px] border px-1.5 py-0.5" style={{ color: tile.cat === 'Creation' ? '#2d7a47' : '#1a6b4e', borderColor: tile.cat === 'Creation' ? '#2d7a47' : '#1a6b4e' }}>{tile.cat}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* CONTEXT BRIDGE */}
      {mode === 'bridge' && (<ContextBridgePanel cityId={cityId} wardId={wardId} wardName={wardName} />)}
    </div>
  );
}
